import { DialogTitle, IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	useDeleteInvoicePaymentMutation,
	useDeleteCreditNotePaymentMutation,
	useDeleteCorrectedInvoicePaymentMutation,
	useDeleteCorrectedCreditNotePaymentMutation,
	useGetAllInvoicePaymentsLazyQuery,
	useGetAllCreditNotePaymentsLazyQuery,
	useGetAllCorrectedInvoicePaymentsLazyQuery,
	useGetAllCorrectedCreditNotePaymentsLazyQuery,
	useRecordInvoicePaymentMutation,
	useRecordCreditNotePaymentMutation,
	useRecordCorrectedInvoicePaymentMutation,
	useRecordCorrectedCreditNotePaymentMutation,
	PaymentItem,
	DocumentType,
} from "@repo/server-api";
import { useTranslation } from "next-i18next";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import { Add, Delete } from "@mui/icons-material";
import { PayData, PayDialog, PaymentDialogHandle } from "./PaymentRecordForwardRefDialog";
import { formatDate } from "@repo/legacy/components/helper/Index";
import { useNotification, useCurrencyFormatter } from "@repo/shared/components";

export type PaymentsDialogShowArgs = {
	invoiceId: string;
	type: DocumentType;
	grossTotal: number;
	refresh?: () => void;
	className?: string;
};

export type PaymentsDialogHandle = {
	show: (args: PaymentsDialogShowArgs) => void;
	close: () => void;
};

export const PaymentsDialog = forwardRef<PaymentsDialogHandle>((_props, ref) => {
	const { t, i18n } = useTranslation();
	const { showNotification } = useNotification();
	const formatCurrency = useCurrencyFormatter();

	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<PaymentsDialogShowArgs | null>(null);
	const [payments, setPayments] = useState<PaymentItem[]>([]);

	const invoiceId = useMemo(() => options?.invoiceId, [options]);
	const type = useMemo(() => options?.type, [options]);

	const payRef = useRef<PaymentDialogHandle | null>(null);

	const [getInvoicePayments] = useGetAllInvoicePaymentsLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			invoiceId: options?.invoiceId || "",
		},
		onCompleted: (data) => {
			setPayments(data.allInvoicePayments as unknown as PaymentItem[]);
		},
	});
	const [getCreditNotePayments] = useGetAllCreditNotePaymentsLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			creditNoteId: options?.invoiceId || "",
		},
		onCompleted: (data) => {
			setPayments(data.allCreditNotePayments as unknown as PaymentItem[]);
		},
	});
	const [getCorrectedInvoicePayments] = useGetAllCorrectedInvoicePaymentsLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			correctedInvoiceId: options?.invoiceId || "",
		},
		onCompleted: (data) => {
			setPayments(data.allCorrectedInvoicePayments as unknown as PaymentItem[]);
		},
	});
	const [getCorrectedCreditNotePayments] = useGetAllCorrectedCreditNotePaymentsLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			correctedCreditNoteId: options?.invoiceId || "",
		},
		onCompleted: (data) => {
			setPayments(data.allCorrectedCreditNotePayments as unknown as PaymentItem[]);
		},
	});

	const refresh = () => {
		if (!invoiceId) return;
		switch (type) {
			case DocumentType.Invoice:
				getInvoicePayments({ variables: { invoiceId } });
				break;
			case DocumentType.CreditNote:
				getCreditNotePayments({ variables: { creditNoteId: invoiceId } });
				break;
			case DocumentType.CorrectedInvoice:
				getCorrectedInvoicePayments({ variables: { correctedInvoiceId: invoiceId } });
				break;
			case DocumentType.CorrectedCreditNote:
				getCorrectedCreditNotePayments({ variables: { correctedCreditNoteId: invoiceId } });
				break;
			default:
				break;
		}
	};

	const [deleteInvoicePayment] = useDeleteInvoicePaymentMutation({
		fetchPolicy: "no-cache",
	});
	const [deleteCreditNotePayment] = useDeleteCreditNotePaymentMutation({
		fetchPolicy: "no-cache",
	});
	const [deleteCorrectedInvoicePayment] = useDeleteCorrectedInvoicePaymentMutation({
		fetchPolicy: "no-cache",
	});
	const [deleteCorrectedCreditNotePayment] = useDeleteCorrectedCreditNotePaymentMutation({
		fetchPolicy: "no-cache",
	});
	const deletePaymentSuccess = (res: { boolean: boolean }) => {
		if (res.boolean) {
			showNotification(t("payment-deleted-successfully"), "success");
			refresh();
		} else {
			showNotification(t("failed-to-delete-payment"), "error");
		}
	};

	const handleDeletePayment = (paymentId: string) => {
		const id = invoiceId!;
		switch (type) {
			case DocumentType.Invoice:
				deleteInvoicePayment({
					variables: { invoiceId: id, invoicePaymentId: paymentId },
				})
					.then((res) => {
						deletePaymentSuccess(res.data?.deleteInvoicePayment as { boolean: boolean });
					})
					.catch((error) => {
						showNotification(error.message, "error");
					});
				break;
			case DocumentType.CreditNote:
				deleteCreditNotePayment({
					variables: { creditNoteId: id, creditNotePaymentId: paymentId },
				})
					.then((res: any) => {
						deletePaymentSuccess(
							res.data?.deleteCreditNotePayment as { boolean: boolean },
						);
					})
					.catch((error: Error) => {
						showNotification(error.message, "error");
					});
				break;
			case DocumentType.CorrectedInvoice:
				deleteCorrectedInvoicePayment({
					variables: { correctedInvoiceId: id, paymentId },
				})
					.then((res) => {
						deletePaymentSuccess(
							res.data?.deleteCorrectedInvoicePayment as { boolean: boolean },
						);
					})
					.catch((error) => {
						showNotification(error.message, "error");
					});
				break;
			case DocumentType.CorrectedCreditNote:
				deleteCorrectedCreditNotePayment({
					variables: { correctedCreditNoteId: id, paymentId },
				})
					.then((res) => {
						deletePaymentSuccess(
							res.data?.deleteCorrectedCreditNotePayment as { boolean: boolean },
						);
					})
					.catch((error) => {
						showNotification(error.message, "error");
					});
				break;
		}
	};

	// expose imperative API
	useImperativeHandle(
		ref,
		() => ({
			show: (args: PaymentsDialogShowArgs) => {
				setOptions(args);
				setOpen(true);
			},
			close: () => {
				handleCloseInternal();
			},
		}),
		[],
	);

	useEffect(() => {
		if (open && options?.invoiceId && type) {
			refresh();
		}
	}, [open, options?.invoiceId, type]);

	const handleCloseInternal = () => {
		if (options?.refresh) options.refresh();
		setOpen(false);
		setOptions(null);
	};

	const handleOkClick = () => {
		if (options?.refresh) options.refresh();
		setOpen(false);
		setOptions(null);
	};

	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: "paymentDate",
				headerName: t("payment-date"),
				width: 150,
				renderCell: (params) => <span>{formatDate(params.value, i18n.language)}</span>,
			},
			{
				field: "amount",
				headerName: t("amount"),
				width: 130,
				renderCell: (params) => <span>{formatCurrency(params.value)}</span>,
			},
			{ field: "paymentMethod", headerName: t("payment-method"), width: 150 },
			{ field: "notes", headerName: t("notes"), width: 200 },
			{
				field: "action",
				headerName: t("actions"),
				width: 150,
				renderCell: (params) => (
					<IconButton
						size="small"
						onClick={() => handleDeletePayment(params.row.id)}>
						<Delete fontSize="small" />
					</IconButton>
				),
			},
		],
		[t, payments],
	);

	const [recordPayment] = useRecordInvoicePaymentMutation();
	const [recordCreditNotePayment] = useRecordCreditNotePaymentMutation();
	const [recordCorrectedInvoicePayment] = useRecordCorrectedInvoicePaymentMutation();
	const [recordCorrectedCreditNotePayment] = useRecordCorrectedCreditNotePaymentMutation();

	const handlePayment = (documentId: string, payData: PayData) => {
		const variables = {
			amount: payData.amount,
			paymentDate: payData.paymentDate,
			paymentMethod: payData.paymentMethod,
			notes: payData.notes,
		};

		switch (type) {
			case DocumentType.CreditNote:
				recordCreditNotePayment({
					variables: { creditNoteId: documentId, ...variables },
				}).then(() => {
					refresh();
				});
				break;
			case DocumentType.CorrectedInvoice:
				recordCorrectedInvoicePayment({
					variables: { correctedInvoiceId: documentId, ...variables },
				}).then(() => {
					refresh();
				});
				break;
			case DocumentType.CorrectedCreditNote:
				recordCorrectedCreditNotePayment({
					variables: { correctedCreditNoteId: documentId, ...variables },
				}).then(() => {
					refresh();
				});
				break;
			default:
				recordPayment({
					variables: { invoiceId: documentId, ...variables },
				}).then(() => {
					refresh();
				});
				break;
		}
	};

	const paidAmount = useMemo(() => {
		return payments.reduce((sum, p) => sum + (p.amount || 0), 0);
	}, [payments]);

	const remainingAmount = useMemo(() => {
		return (options?.grossTotal || 0) - paidAmount;
	}, [options?.grossTotal, paidAmount]);

	const openCreatePayment = (invoiceId: string) => {
		payRef.current?.show({
			invoiceId,
			className: "",
			onPay: handlePayment,
			invoiceTotal: options?.grossTotal || 0,
			remainingAmount,
		});
	};

	return (
		<>
			<PayDialog ref={payRef} />

			<Dialog
				open={open}
				maxWidth="md"
				className={options?.className}
				data-testid="payments-dialog">
				<DialogTitle
					style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					<span>{t("payments")}</span>
					<Button
						className="greenButton right"
						variant="contained"
						data-testid="create-payment-button"
						onClick={() => openCreatePayment(options?.invoiceId!)}>
						{t("create-payment")} <Add fontSize="small" />
					</Button>
				</DialogTitle>
				<DialogContent>
					{payments.length === 0 ? (
						<div data-testid="no-payments-message">{t("no-payments-found")}</div>
					) : (
						<DataGridPremium
							autoHeight
							rows={payments as unknown as PaymentItem[]}
							columns={columns}
							disableColumnMenu
							hideFooter
							data-testid="payments-grid"
						/>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						variant="contained"
						onClick={handleOkClick}
						data-testid="payments-dialog-ok-button">
						{t("ok")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
});
